import React from 'react';
import { formatPrice, formatPercentage, getCurrencySymbol } from '@/utils/formatting';

interface PriceDisplayProps {
  price: number;
  currency?: string;
  size?: 'small' | 'medium' | 'large';
  showCurrency?: boolean;
}

export const PriceDisplay: React.FC<PriceDisplayProps> = ({
  price,
  currency = 'USDT',
  size = 'medium',
  showCurrency = true,
}) => {
  const sizeStyles = {
    small: { fontSize: '12px', fontWeight: 400 },
    medium: { fontSize: '16px', fontWeight: 500 },
    large: { fontSize: '24px', fontWeight: 600 },
  };

  const currencySymbol = getCurrencySymbol(currency);

  return (
    <span
      style={{
        color: 'var(--tg-theme-text-color)',
        ...sizeStyles[size],
      }}
    >
      {showCurrency && currencySymbol}
      {formatPrice(price)}
    </span>
  );
};

interface PriceChangeProps {
  change: number;
  size?: 'small' | 'medium' | 'large';
}

export const PriceChange: React.FC<PriceChangeProps> = ({ change, size = 'medium' }) => {
  const isPositive = change >= 0;

  const sizeStyles = {
    small: { fontSize: '11px' },
    medium: { fontSize: '13px' },
    large: { fontSize: '16px' },
  };

  return (
    <span
      style={{
        color: isPositive ? '#34c759' : '#ff3b30',
        fontWeight: 500,
        ...sizeStyles[size],
      }}
    >
      {formatPercentage(change)}
    </span>
  );
};

interface SignalPriceInfoProps {
  startPrice: number;
  currentPrice?: number;
  currency: string;
}

export const SignalPriceInfo: React.FC<SignalPriceInfoProps> = ({
  startPrice,
  currentPrice,
  currency,
}) => {
  const change = currentPrice ? ((currentPrice - startPrice) / startPrice) * 100 : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
      {currentPrice ? (
        <>
          <PriceDisplay price={currentPrice} currency={currency} size="medium" />
          <PriceChange change={change} size="small" />
        </>
      ) : (
        <span style={{ color: 'var(--tg-theme-hint-color)', fontSize: '12px' }}>
          Start: {getCurrencySymbol(currency)}{formatPrice(startPrice)}
        </span>
      )}
    </div>
  );
};
